const SERVER = import.meta.env.VITE_API_URL;
import axios from "../../configs/axios.config";

const createNewDriver = async (formdata) => {
  try {
    let token = localStorage.getItem("prima_session");

    if (!token) {
      throw new Error("Token not found");
    }

    let response = await axios.post(`${SERVER}/inventory/newDriver`, formdata, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });

    if (response.status === 401 || response.status === 403) {
      throw new Error("Unauthorized user");
    }

    return response.data?.data;
  } catch (error) {
    console.error(
      "Error creating driver:",
      error.response?.data?.message || error.message,
    );

    const hasMeaningfulMessage =
      typeof error.message === "string" && error.message.trim().length > 0;

    // Preserve backend HTTP errors so the caller can show the exact response detail.
    if (error.response) {
      throw error;
    }

    // Network / no-response / empty-message cases should use the fallback.
    if (!hasMeaningfulMessage) {
      throw new Error(
        "Unable to reach server while creating driver. Please check network and API URL.",
      );
    }

    throw new Error(error.message);
  }
};

const getDriversList = async (page, limit, query, statusFilter = "", vehicleSegmentId = "") => {
  try {
    let token = localStorage.getItem("prima_session");

    if (!token) {
      throw new Error("Token not found");
    }

    const params = new URLSearchParams({
      page,
      limit,
      query: query || "",
    });

    if (statusFilter && statusFilter !== "") {
      params.append("statusFilter", statusFilter);
    }
    
    if (vehicleSegmentId && vehicleSegmentId !== "") {
      params.append("vehicleSegmentId", vehicleSegmentId);
    }

    let response = await axios.get(
      `${SERVER}/inventory/drivers?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    );

    if (response.status === 401 || response.status === 403) {
      throw new Error("Unauthorized user");
    }

    return response.data;
  } catch (error) {
    console.error(
      "Error fetching drivers:",
      error.response?.data?.message || error.message,
    );
    throw new Error(error.response?.data?.message || "Failed to fetch drivers");
  }
};
const getDriverDetails = async (id) => {
  try {
    let token = localStorage.getItem("prima_session");

    if (!token) {
      throw new Error("Token not found");
    }
    let response = await axios.get(`${SERVER}/inventory/driver/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });

    if (response.status === 401 || response.status === 403) {
      throw new Error("Unauthorized user");
    }

    return response.data;
  } catch (error) {
    console.error(
      "Error fetching roles:",
      error.response?.data?.message || error.message,
    );
    throw new Error(error.response?.data?.message || "Failed to fetch roles");
  }
};

const updateDriverDetails = async (id, formdata) => {
  try {
    let token = localStorage.getItem("prima_session");

    if (!token) {
      throw new Error("Token not found");
    }
    let response = await axios.patch(
      `${SERVER}/inventory/driver/${id}`,
      formdata,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );

    if (response.status === 401 || response.status === 403) {
      throw new Error("Unauthorized user");
    }

    return response.data;
  } catch (error) {
    console.error(
      "Error fetching roles:",
      error.response?.data?.message || error.message,
    );
    throw new Error(error.response?.data?.message || "Failed to fetch roles");
  }
};

const deleteDriver = async (id) => {
  try {
    let token = localStorage.getItem("prima_session");

    if (!token) {
      throw new Error("Token not found");
    }
    let response = await axios.delete(`${SERVER}/inventory/driver/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });

    if (response.status === 401 || response.status === 403) {
      throw new Error("Unauthorized user");
    }

    return response.data;
  } catch (error) {
    console.error(
      "Error deleting driver:",
      error.response?.data?.message || error.message,
    );
    throw new Error(error.response?.data?.message || "Failed to delete driver");
  }
};


const getDriverTrips = async (driverId) => {
  try {
    const token = localStorage.getItem("prima_session");
    if (!token) {
      throw new Error("Token not found");
    }
    const response = await axios.get(
      `${SERVER}/inventory/driver/${driverId}/trips`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || "API Error");
    } else {
      throw new Error(error.message);
    }
  }
};

const getDriverAuditLogs = async (driverId, page = 1, limit = 50) => {
  try {
    const token = localStorage.getItem("prima_session");
    if (!token) {
      throw new Error("Token not found");
    }
    const response = await axios.get(
      `${SERVER}/inventory/driver/${driverId}/audit-logs?page=${page}&limit=${limit}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || "API Error");
    } else {
      throw new Error(error.message);
    }
  }
};

const checkDriverPhone = async (mobileNumber) => {
  try {
    const token = localStorage.getItem("prima_session");
    if (!token) {
      throw new Error("Token not found");
    }
    const response = await axios.get(
      `${SERVER}/inventory/drivers/check-phone?mobileNumber=${mobileNumber}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error checking driver phone:", error.response?.data?.message || error.message);
    throw new Error(error.response?.data?.message || "Failed to check phone number");
  }
};

const getDriverFleets = async (driverId) => {
  try {
    const token = localStorage.getItem("prima_session");
    if (!token) throw new Error("Token not found");
    const response = await axios.get(`${SERVER}/inventory/driver/${driverId}/fleets`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch driver fleets");
  }
};

export {
  getDriverTrips,
  getDriverAuditLogs,
  checkDriverPhone,
  createNewDriver,
  getDriversList,
  getDriverDetails,
  updateDriverDetails,
  deleteDriver,
  getDriverFleets,
};
